<script setup lang="ts">
import { onBeforeUnmount, watch } from "vue";
import { XMarkIcon } from "@heroicons/vue/24/outline";

/**
 * Generic modal for destructive confirmation. Extracted alongside the
 * delete-job feature; reused by future destructive actions (rename
 * collision, sign-out with pending uploads, etc.).
 *
 * Behavior:
 *  - Backdrop click → cancel (safe). The dialog's inner click stops
 *    propagation so click-and-drag inside doesn't dismiss.
 *  - ESC → cancel (handled at the DOCUMENT level when open; focus
 *    is never programmatically moved into the dialog, so attaching
 *    keydown on the dialog element alone wouldn't fire in real
 *    browsers — caught by silent-failure-hunter + pr-test-analyzer
 *    in the PR #810 review).
 *  - Primary button is the destructive action; cancel is the safe
 *    default.
 */
const props = defineProps<{
  open: boolean;
  title: string;
  body: string;
  /** Primary (destructive) action label, e.g. "Delete forever". */
  primaryLabel: string;
  /** Cancel label, default "Cancel". */
  cancelLabel?: string;
  /** Shown while the primary action is in flight. */
  inFlight?: boolean;
}>();

const emit = defineEmits<{
  confirm: [];
  cancel: [];
}>();

function onBackdrop(event: MouseEvent) {
  if (event.target === event.currentTarget) emit("cancel");
}

function onDocKeyDown(event: KeyboardEvent) {
  if (event.key === "Escape") emit("cancel");
}

// Attach the ESC handler at the document level only while the dialog
// is open. Mirrors JobRowMenu's pattern; addresses the real-browser
// focus-routing issue where keydown on the dialog div never fires
// because focus stays on whatever button opened the dialog.
watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      document.addEventListener("keydown", onDocKeyDown);
    } else {
      document.removeEventListener("keydown", onDocKeyDown);
    }
  },
  { immediate: true },
);
onBeforeUnmount(() => document.removeEventListener("keydown", onDocKeyDown));
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition ease-out duration-150"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition ease-in duration-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="open"
        data-testid="confirm-dialog"
        class="fixed inset-0 z-40 flex items-center justify-center p-4"
        style="background-color: var(--color-backdrop)"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        @click="onBackdrop"
      >
        <div
          class="relative w-full max-w-md rounded-lg bg-bg-main shadow-xl p-6"
          @click.stop
        >
          <button
            type="button"
            data-testid="confirm-dialog-close"
            class="absolute right-4 top-4 rounded p-1 text-text-tertiary hover:bg-bg-hover transition-colors"
            aria-label="Close"
            @click="emit('cancel')"
          >
            <XMarkIcon class="h-5 w-5" aria-hidden="true" />
          </button>
          <h2
            id="confirm-dialog-title"
            data-testid="confirm-dialog-title"
            class="text-lg font-semibold text-text-primary mb-3 pr-8"
          >
            {{ title }}
          </h2>
          <p data-testid="confirm-dialog-body" class="text-sm text-text-secondary mb-6">
            {{ body }}
          </p>
          <div class="flex justify-end gap-2">
            <button
              type="button"
              data-testid="confirm-dialog-cancel"
              ref="cancelBtn"
              :disabled="inFlight"
              class="rounded-md border border-border bg-bg-main px-4 py-2 text-sm font-medium text-text-primary hover:bg-bg-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              @click="emit('cancel')"
            >
              {{ cancelLabel ?? "Cancel" }}
            </button>
            <button
              type="button"
              data-testid="confirm-dialog-primary"
              :disabled="inFlight"
              class="rounded-md bg-error px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              @click="emit('confirm')"
            >
              {{ inFlight ? "Working…" : primaryLabel }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
